import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { CalendarCheck, Link2, Copy, Unlink, MessageCircle } from 'lucide-react';

/** Collegamento (facoltativo) del Google Calendar personale di un operatore. */
export const StaffCalendarLink = ({ member, link, onChanged }) => {
  const { API, getAuthHeaders } = useAuth();
  const [inviteUrl, setInviteUrl] = useState('');
  const [busy, setBusy] = useState(false);

  const createInvite = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/staff/${member.id}/calendar/invite`, {}, getAuthHeaders());
      setInviteUrl(`${window.location.origin}${res.data.path}`);
      toast.success('Link creato: mandalo a ' + member.name);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Creazione link non riuscita');
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/staff/${member.id}/calendar`, getAuthHeaders());
      toast.success('Google Calendar scollegato');
      setInviteUrl('');
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Operazione non riuscita');
    } finally {
      setBusy(false);
    }
  };

  const copy = () => {
    navigator.clipboard?.writeText(inviteUrl);
    toast.success('Link copiato');
  };

  if (link?.connected) {
    return (
      <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2" data-testid={`staff-calendar-connected-${member.id}`}>
        <CalendarCheck className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
        <span className="min-w-0 flex-1 truncate text-xs">
          Google Calendar collegato
          {link.google_email ? <span className="text-muted-foreground"> · {link.google_email}</span> : null}
        </span>
        <Button variant="ghost" size="sm" onClick={disconnect} disabled={busy} className="h-8 text-xs" data-testid={`staff-calendar-disconnect-${member.id}`}>
          <Unlink className="mr-1.5 h-3.5 w-3.5" />
          Scollega
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-2" data-testid={`staff-calendar-row-${member.id}`}>
      {inviteUrl ? (
        <div className="space-y-2 rounded-lg border bg-muted/30 p-3">
          <p className="text-xs text-muted-foreground">
            Manda questo link a {member.name}: lo apre e autorizza il proprio Google Calendar. Vale 7 giorni.
          </p>
          <div className="flex gap-2">
            <Input readOnly value={inviteUrl} className="h-9 text-xs" data-testid={`staff-calendar-invite-url-${member.id}`} />
            <Button variant="outline" size="sm" className="h-9 shrink-0" onClick={copy} data-testid={`staff-calendar-copy-${member.id}`}>
              <Copy className="h-3.5 w-3.5" />
            </Button>
            {member.phone && (
              <a
                href={`https://wa.me/${member.phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Ciao ${member.name}, collega qui il tuo Google Calendar al salone: ${inviteUrl}`)}`}
                target="_blank"
                rel="noreferrer"
                data-testid={`staff-calendar-whatsapp-${member.id}`}
              >
                <Button variant="outline" size="sm" className="h-9 shrink-0">
                  <MessageCircle className="h-3.5 w-3.5" />
                </Button>
              </a>
            )}
          </div>
        </div>
      ) : (
        <Button variant="outline" size="sm" onClick={createInvite} disabled={busy} className="h-8 text-xs" data-testid={`staff-calendar-connect-${member.id}`}>
          <Link2 className="mr-1.5 h-3.5 w-3.5" />
          Collega Google Calendar
        </Button>
      )}
    </div>
  );
};
